import type { ComponentType } from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Ban,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  CircleX,
  Copy,
  CopyPlus,
  Download,
  ExternalLink,
  Eye,
  Languages,
  Mail,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Printer,
  RefreshCw,
  Send,
  Share2,
  Star,
  StarOff,
  Trash2,
  Upload,
  X,
  EyeOff,
} from 'lucide-react';
import WhatsAppIcon from './WhatsAppIcon';

/**
 * Registro único das ações de item do sistema. Toda tela usa o MESMO ícone, cor e rótulo
 * para a mesma ação — não escolha ícone à mão; adicione aqui se faltar.
 *
 * Convenções:
 * - `edit` é sempre Pencil; `delete` sempre Trash2 (vermelho) e sempre a última ação da linha.
 * - `view` (Eye) abre detalhe DENTRO do sistema; `open` (ExternalLink) abre em nova aba / fora.
 * - `approve` (CircleCheck) também serve para "Marcar pago"/"Aceito" — passe `label`.
 * - `revoke` (Ban) invalida link/convite; `cancel` (CircleX) cancela reserva/agendamento.
 * - `activate`/`deactivate` (Power/PowerOff) para qualquer liga/desliga de registro.
 * - Ação de domínio sem equivalente (Notas, Procuração…): omita `action` e passe `icon` + `label`.
 * - `remove` (X) tira um item de um formulário/lista sem apagar o registro (ex.: anexo, parcela).
 */
export type ActionKey =
  | 'edit'
  | 'delete'
  | 'remove'
  | 'copy'
  | 'copyLink'
  | 'open'
  | 'view'
  | 'download'
  | 'upload'
  | 'share'
  | 'duplicate'
  | 'approve'
  | 'reject'
  | 'revoke'
  | 'cancel'
  | 'expand'
  | 'collapse'
  | 'moveUp'
  | 'moveDown'
  | 'publish'
  | 'unpublish'
  | 'feature'
  | 'unfeature'
  | 'close'
  | 'add'
  | 'send'
  | 'whatsapp'
  | 'email'
  | 'print'
  | 'refresh'
  | 'activate'
  | 'deactivate'
  | 'translate'
  | 'back'
  | 'more';

export type ActionVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger';

export type IconComponent = ComponentType<{ className?: string }>;

export interface ActionDef {
  icon: IconComponent;
  variant: ActionVariant;
}

export const ACTIONS: Record<ActionKey, ActionDef> = {
  edit: { icon: Pencil, variant: 'primary' },
  delete: { icon: Trash2, variant: 'danger' },
  remove: { icon: X, variant: 'danger' },
  copy: { icon: Copy, variant: 'default' },
  copyLink: { icon: Copy, variant: 'default' },
  open: { icon: ExternalLink, variant: 'default' },
  view: { icon: Eye, variant: 'default' },
  download: { icon: Download, variant: 'default' },
  upload: { icon: Upload, variant: 'default' },
  share: { icon: Share2, variant: 'default' },
  duplicate: { icon: CopyPlus, variant: 'default' },
  approve: { icon: CircleCheck, variant: 'success' },
  reject: { icon: CircleX, variant: 'danger' },
  revoke: { icon: Ban, variant: 'warning' },
  cancel: { icon: CircleX, variant: 'warning' },
  expand: { icon: ChevronDown, variant: 'default' },
  collapse: { icon: ChevronUp, variant: 'default' },
  moveUp: { icon: ArrowUp, variant: 'default' },
  moveDown: { icon: ArrowDown, variant: 'default' },
  publish: { icon: Eye, variant: 'success' },
  unpublish: { icon: EyeOff, variant: 'warning' },
  feature: { icon: Star, variant: 'warning' },
  unfeature: { icon: StarOff, variant: 'default' },
  close: { icon: X, variant: 'default' },
  add: { icon: Plus, variant: 'primary' },
  send: { icon: Send, variant: 'primary' },
  whatsapp: { icon: WhatsAppIcon, variant: 'success' },
  email: { icon: Mail, variant: 'default' },
  print: { icon: Printer, variant: 'default' },
  refresh: { icon: RefreshCw, variant: 'default' },
  activate: { icon: Power, variant: 'success' },
  deactivate: { icon: PowerOff, variant: 'warning' },
  translate: { icon: Languages, variant: 'default' },
  back: { icon: ArrowLeft, variant: 'default' },
  more: { icon: MoreHorizontal, variant: 'default' },
};

/** Ordem canônica dentro de <RowActions>: menor vem antes. Excluir sempre por último. */
export const ACTION_ORDER: Partial<Record<ActionKey, number>> = {
  delete: 100,
  remove: 90,
};

/** Tamanhos de ícone oficiais. */
export const ICON_SIZE = {
  /** Ícone de ação de linha: maior no mobile (há rótulo embaixo), compacto no desktop. */
  row: 'w-5 h-5 md:w-4 md:h-4',
  /** Ícone isolado (IconButton). */
  icon: 'w-5 h-5',
  /** Ícone dentro de botão com texto. */
  button: 'w-4 h-4',
} as const;
